import { create } from 'zustand';
import fetchWithTenant from '../utils/fetchWithTenant';
import { getIsStandalone } from '../hooks/useIsStandalone';

const useTutorialStore = create((set, get) => ({
  activeTutorial: null,
  isActive: false,
  progress: new Map(),
  loaded: false,

  loadProgress: async () => {
    try {
      const res = await fetchWithTenant('/api/tutorials/progress');
      if (!res.ok) return;
      const records = await res.json();
      const map = new Map();
      for (const r of records) map.set(r.module, r);
      set({ progress: map, loaded: true });
    } catch (err) {
      console.error('[tutorialStore] loadProgress failed', err);
    }
  },

  startTutorial: (module, steps) => {
    // PWA: the tutorial overlay mis-sizes and cannot be exited in the
    // installed app, so disable it entirely there. Every launch path
    // (auto-prompt, help buttons, site-setup, profile menu) funnels through
    // this function, so this single guard no-ops all of them. The buttons
    // stay visible but do nothing on standalone; desktop and browser tabs
    // are unaffected.
    // getIsStandalone() also checks navigator.standalone, so this now correctly
    // no-ops on the iOS home-screen PWA (where the media query alone was false).
    if (getIsStandalone()) return;
    if (!steps || steps.length === 0) return;
    set({
      activeTutorial: { module, steps, currentIndex: 0 },
      isActive: true,
    });
  },

  nextStep: () => {
    const { activeTutorial, completeTutorial } = get();
    if (!activeTutorial) return;
    const nextIndex = activeTutorial.currentIndex + 1;
    if (nextIndex >= activeTutorial.steps.length) {
      completeTutorial();
      return;
    }
    set({
      activeTutorial: { ...activeTutorial, currentIndex: nextIndex },
    });
  },

  prevStep: () => {
    const { activeTutorial } = get();
    if (!activeTutorial) return;
    const prevIndex = Math.max(0, activeTutorial.currentIndex - 1);
    set({
      activeTutorial: { ...activeTutorial, currentIndex: prevIndex },
    });
  },

  skipTutorial: async () => {
    const { activeTutorial, progress } = get();
    if (!activeTutorial) return;
    const module = activeTutorial.module;
    set({ activeTutorial: null, isActive: false });
    try {
      const res = await fetchWithTenant('/api/tutorials/progress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ module, dismissed: true }),
      });
      if (res.ok) {
        const record = await res.json();
        const map = new Map(progress);
        map.set(module, record);
        set({ progress: map });
      }
    } catch (err) {
      console.error('[tutorialStore] skipTutorial failed', err);
    }
  },

  completeTutorial: async () => {
    const { activeTutorial, progress } = get();
    if (!activeTutorial) return;
    const module = activeTutorial.module;
    set({ activeTutorial: null, isActive: false });
    try {
      const res = await fetchWithTenant('/api/tutorials/progress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ module, completed: true }),
      });
      if (res.ok) {
        const record = await res.json();
        const map = new Map(progress);
        map.set(module, record);
        set({ progress: map });
      }
    } catch (err) {
      console.error('[tutorialStore] completeTutorial failed', err);
    }
  },

  isModuleCompleted: (module) => {
    const rec = get().progress.get(module);
    return !!(rec && rec.completed);
  },

  isModuleDismissed: (module) => {
    const rec = get().progress.get(module);
    return !!(rec && rec.dismissed);
  },

  resetModule: async (module) => {
    try {
      await fetchWithTenant(`/api/tutorials/progress/${encodeURIComponent(module)}`, {
        method: 'DELETE',
      });
      const map = new Map(get().progress);
      map.delete(module);
      set({ progress: map });
    } catch (err) {
      console.error('[tutorialStore] resetModule failed', err);
    }
  },

  resetAll: async () => {
    try {
      await fetchWithTenant('/api/tutorials/progress', { method: 'DELETE' });
      set({ progress: new Map() });
    } catch (err) {
      console.error('[tutorialStore] resetAll failed', err);
    }
  },
}));

export default useTutorialStore;
